import React, { useRef, useState } from 'react';
import { ClockModel } from 'shared/types';
import { formatTime, parseISODateFormat } from 'utils/date-utils';
import HoverTip from './HoverTip';
import {
  CLOCK_COLORS,
  StripWrap,
  StripRuler,
  StripTickLabel,
  StripTrack,
  StripGridline,
  StripPlayhead,
  StripBlock,
  StripActive,
  StripLeRow,
  StripLeBand,
  StripAnswerTick,
} from './styles';

type Props = {
  model: ClockModel;
};

// Tick interval giving roughly 6–12 marks across the bar (video-timeline feel).
const niceStep = (totalSec: number): number => {
  const steps = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200];
  return steps.find(s => totalSec / s <= 12) ?? totalSec / 8;
};

/** Linear companion to the clock with a scrubbable video-timeline hover. */
const SessionTimelineStrip: React.FC<Props> = ({ model }): JSX.Element => {
  const total = model.totalSec || 1;
  const pct = (frac: number): number => Math.max(0, Math.min(100, frac * 100));
  const trackRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ x: number; y: number; frac: number } | null>(null);

  // Ruler ticks.
  const step = niceStep(total);
  const ticks: number[] = [];
  for (let t = 0; t <= total + 0.001; t += step) ticks.push(t);

  const onMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHover({ x: e.clientX, y: e.clientY, frac });
  };

  const renderTip = (frac: number): React.ReactNode => {
    const sec = frac * total;
    const seg = model.segments.find(s => frac >= s.startFrac && frac <= s.endFrac);
    const le = model.leBands.find(b => frac >= b.startFrac && frac <= b.endFrac);
    const ans = model.answers.find(a => Math.abs(a.frac - frac) < 0.012);
    const timeOfDay = parseISODateFormat(new Date(model.startMs + sec * 1000), 'h:mm:ssa');

    // Position-aware: report active vs idle based on where in the station the
    // cursor sits, not the whole-station summary.
    let segLine: React.ReactNode = null;
    if (seg) {
      if (seg.kind === 'station') {
        const station = `Station ${seg.stationNumber ?? 'Intro'}`;
        const activeEndFrac = total > 0 ? seg.startFrac + seg.activeSec / total : seg.startFrac;
        if (seg.hasProgress && frac <= activeEndFrac) {
          segLine = `${station} · Active learning\n${formatTime(seg.activeSec)} active`;
        } else {
          segLine = seg.hasProgress
            ? `${station} · Idle\n${formatTime(seg.idleSec)} idle`
            : `${station} · Idle\nno active learning recorded`;
        }
      } else {
        segLine = seg.kind === 'lobby' ? 'Lobby / loading' : 'Idle (between stations)';
      }
    }

    return (
      <>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{formatTime(sec)}</div>
        {segLine && <div>{segLine}</div>}
        {le && (
          <div style={{ opacity: 0.85 }}>
            {(le.leName ?? 'Learning experience') + ` · ${formatTime(sec - le.startSec)} in`}
          </div>
        )}
        {ans && (
          <div style={{ marginTop: 2 }}>
            {`◆ Answer · Station ${ans.stationNumber ?? '—'} · ${
              ans.isCorrect === true ? 'correct' : ans.isCorrect === false ? 'incorrect' : 'unknown'
            }`}
          </div>
        )}
        <div
          style={{
            marginTop: 4,
            paddingTop: 4,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            opacity: 0.75,
          }}
        >
          {timeOfDay}
        </div>
      </>
    );
  };

  return (
    <StripWrap>
      <StripRuler>
        {ticks.map((t, i) => {
          const left = pct(t / total);
          const transform = i === 0 ? 'translateX(0)' : left >= 99.5 ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <StripTickLabel key={`rl-${i}`} style={{ left: `${left}%`, transform }}>
              {formatTime(t)}
            </StripTickLabel>
          );
        })}
      </StripRuler>

      <StripTrack
        ref={trackRef}
        onMouseMove={onMove}
        onMouseLeave={(): void => setHover(null)}
      >
        {ticks.map((t, i) => (
          <StripGridline key={`gl-${i}`} $left={pct(t / total)} />
        ))}

        {model.segments.map(seg => {
          const left = pct(seg.startFrac);
          const width = pct(seg.endFrac) - left;
          if (width <= 0) return null;

          if (seg.kind === 'station') {
            const activeWidthPct = seg.durationSec > 0 ? (seg.activeSec / seg.durationSec) * 100 : 0;
            return (
              <StripBlock key={seg.id} $left={left} $width={width} $bg={CLOCK_COLORS.idle}>
                {activeWidthPct > 0 && <StripActive $width={activeWidthPct} />}
              </StripBlock>
            );
          }

          return (
            <StripBlock
              key={seg.id}
              $left={left}
              $width={width}
              $bg={seg.kind === 'lobby' ? CLOCK_COLORS.lobby : CLOCK_COLORS.track}
            />
          );
        })}

        {model.answers.map(ans => {
          const color =
            ans.isCorrect === true
              ? CLOCK_COLORS.correct
              : ans.isCorrect === false
                ? CLOCK_COLORS.incorrect
                : CLOCK_COLORS.unknownAnswer;
          return <StripAnswerTick key={ans.id} $left={pct(ans.frac)} $bg={color} />;
        })}

        {hover && <StripPlayhead $left={pct(hover.frac)} />}
      </StripTrack>

      {model.leBands.length > 0 && (
        <StripLeRow>
          {model.leBands.map((band, i) => {
            const left = pct(band.startFrac);
            const width = pct(band.endFrac) - left;
            if (width <= 0) return null;
            return (
              <StripLeBand
                key={band.id}
                $left={left}
                $width={width}
                $bg={CLOCK_COLORS.leBands[i % CLOCK_COLORS.leBands.length]}
              />
            );
          })}
        </StripLeRow>
      )}

      {hover && (
        <HoverTip x={hover.x} y={hover.y}>
          {renderTip(hover.frac)}
        </HoverTip>
      )}
    </StripWrap>
  );
};

export default SessionTimelineStrip;
